import React, { useEffect, useState } from "react";

/**
 * Site Settings — edit the announcement bar, raffle nav pill and membership
 * CTA without touching code. Backed by a single `/api/site-settings` doc
 * that is merged with sensible defaults server-side so partial saves never
 * leave the header blank.
 */
export default function SiteSettingsAdmin({ authed }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    authed.get("/site-settings").then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const r = await authed.put("/admin/site-settings", data);
      setData(r.data);
      setMsg("Saved. Refresh the site to see it live.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-ink/50 text-sm">Loading…</div>;

  const setSection = (section, patch) =>
    setData(x => ({ ...x, [section]: { ...(x[section] || {}), ...patch } }));

  return (
    <div className="space-y-6" data-testid="site-settings">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-terracotta text-[10px]">SITE SETTINGS</div>
        <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">Navigation & announcement</h2>
        <p className="mt-2 text-xs text-ink/60 max-w-lg">Edit the top announcement, the raffle pill and the membership CTA. Nothing here changes URLs or breaks existing pages.</p>
      </section>

      <Toggle label="Show announcement bar"
              checked={!!data.announcement?.show}
              onChange={v => setSection("announcement", { show: v })}
              testid="toggle-announcement" />
      {data.announcement?.show && (
        <Box title="Announcement bar" hint="A slim strip above the header. Perfect for the next upcoming event.">
          <I18nRow label="Eyebrow (small label)" value={data.announcement.eyebrow || {}}
                   onChange={v => setSection("announcement", { eyebrow: v })} />
          <I18nRow label="Text" value={data.announcement.text || {}}
                   onChange={v => setSection("announcement", { text: v })} />
          <I18nRow label="CTA label" value={data.announcement.cta_label || {}}
                   onChange={v => setSection("announcement", { cta_label: v })} />
          <TextRow label="CTA URL" value={data.announcement.cta_url || ""}
                   placeholder="/events"
                   onChange={v => setSection("announcement", { cta_url: v })} />
        </Box>
      )}

      <Toggle label="Show RAFFLE pill in header"
              checked={!!data.raffle_nav?.show}
              onChange={v => setSection("raffle_nav", { show: v })}
              testid="toggle-raffle-pill" />
      {data.raffle_nav?.show && (
        <Box title="Raffle nav" hint="Special treatment while the raffle is active. Hide it as soon as it's over.">
          <I18nRow label="Optional small label (e.g. 50 DKK)" value={data.raffle_nav.label || {}}
                   onChange={v => setSection("raffle_nav", { label: v })} />
        </Box>
      )}

      <Toggle label="Show 'Become a member' CTA"
              checked={!!data.membership?.show}
              onChange={v => setSection("membership", { show: v })}
              testid="toggle-membership" />
      {data.membership?.show && (
        <Box title="Membership CTA" hint="The strongest button in the header. Points visitors to becoming part of the community.">
          <I18nRow label="Button label" value={data.membership.label || {}}
                   onChange={v => setSection("membership", { label: v })} />
          <I18nRow label="Hover line (small tooltip on desktop)" value={data.membership.hover_line || {}}
                   onChange={v => setSection("membership", { hover_line: v })} />
          <TextRow label="Destination URL" value={data.membership.url || ""}
                   placeholder="/get-involved"
                   onChange={v => setSection("membership", { url: v })} />
        </Box>
      )}

      <Box title="Navigation visibility" hint="Show or hide each top-level link. Labels are localised.">
        {[
          ["story", "Our Story"],
          ["events", "Events"],
          ["impact", "Impact"],
          ["involved", "Get Involved"],
        ].map(([k, l]) => (
          <div key={k} className="flex items-center gap-3">
            <input type="checkbox" checked={data.nav?.[k]?.show !== false}
                   data-testid={`nav-toggle-${k}`}
                   onChange={e => setSection("nav", { [k]: { ...(data.nav?.[k] || {}), show: e.target.checked } })}
                   className="w-4 h-4 accent-terracotta" />
            <span className="text-sm w-32">{l}</span>
            <input value={data.nav?.[k]?.url || ""} placeholder="/mission"
                   onChange={e => setSection("nav", { [k]: { ...(data.nav?.[k] || {}), url: e.target.value } })}
                   data-testid={`nav-url-${k}`}
                   className="flex-1 border border-line px-2 py-1.5 text-xs" />
          </div>
        ))}
      </Box>

      <Box title="Floating CTA (bottom-right)" hint="The pill that appears while scrolling.">
        <Toggle label="Show floating CTA" checked={data.floating_cta?.show !== false}
                onChange={v => setSection("floating_cta", { show: v })} testid="toggle-floating-cta" />
        {data.floating_cta?.show !== false && (<>
          <I18nRow label="Label (desktop)" value={data.floating_cta?.label || {}}
                   onChange={v => setSection("floating_cta", { label: v })} />
          <I18nRow label="Label (mobile)" value={data.floating_cta?.label_mobile || {}}
                   onChange={v => setSection("floating_cta", { label_mobile: v })} />
          <TextRow label="Destination URL" value={data.floating_cta?.url || ""} placeholder="/join"
                   onChange={v => setSection("floating_cta", { url: v })} />
        </>)}
      </Box>

      <Box title="Cookie banner" hint="Text shown until the visitor accepts. Multilingual.">
        <Toggle label="Show cookie banner" checked={data.cookie_banner?.show !== false}
                onChange={v => setSection("cookie_banner", { show: v })} testid="toggle-cookie" />
        {data.cookie_banner?.show !== false && (<>
          <I18nRow label="Text" value={data.cookie_banner?.text || {}}
                   onChange={v => setSection("cookie_banner", { text: v })} />
          <I18nRow label="Essential-only button" value={data.cookie_banner?.essential_label || {}}
                   onChange={v => setSection("cookie_banner", { essential_label: v })} />
          <I18nRow label="Accept-all button" value={data.cookie_banner?.accept_label || {}}
                   onChange={v => setSection("cookie_banner", { accept_label: v })} />
          <TextRow label="Policy URL" value={data.cookie_banner?.policy_url || "/legal/cookies"}
                   onChange={v => setSection("cookie_banner", { policy_url: v })} />
        </>)}
      </Box>

      <Box title="Contact information" hint="Reused in the footer and legal pages.">
        <TextRow label="Email" value={data.contact?.email || ""}
                 onChange={v => setSection("contact", { email: v })} />
        <TextRow label="CVR" value={data.contact?.cvr || ""}
                 onChange={v => setSection("contact", { cvr: v })} />
        <I18nRow label="Address (footer label)" value={data.contact?.location_label || {}}
                 onChange={v => setSection("contact", { location_label: v })} />
      </Box>

      <Box title="Social links" hint="Instagram is currently shown in header, mobile menu and footer.">
        <TextRow label="Instagram handle" value={data.socials?.instagram_handle || ""}
                 onChange={v => setSection("socials", { instagram_handle: v })} />
        <TextRow label="Instagram URL" value={data.socials?.instagram_url || ""}
                 onChange={v => setSection("socials", { instagram_url: v })} />
        <TextRow label="Facebook URL" value={data.socials?.facebook_url || ""}
                 onChange={v => setSection("socials", { facebook_url: v })} />
        <TextRow label="LinkedIn URL" value={data.socials?.linkedin_url || ""}
                 onChange={v => setSection("socials", { linkedin_url: v })} />
      </Box>

      <Box title="Footer" hint="Tagline, closing line and column labels. Individual link toggles below.">
        <Toggle label="Show footer" checked={data.footer?.show !== false}
                onChange={v => setSection("footer", { show: v })} testid="toggle-footer" />
        {data.footer?.show !== false && (<>
          <I18nRow label="Tagline" value={data.footer?.tagline || {}}
                   onChange={v => setSection("footer", { tagline: v })} />
          <I18nRow label="Closing line" value={data.footer?.closing_line || {}}
                   onChange={v => setSection("footer", { closing_line: v })} />
          <I18nRow label="Column: Explore label" value={data.footer?.links_label || {}}
                   onChange={v => setSection("footer", { links_label: v })} />
          <I18nRow label="Column: Legal label" value={data.footer?.legal_label || {}}
                   onChange={v => setSection("footer", { legal_label: v })} />
          <I18nRow label="Column: Contact label" value={data.footer?.contact_label || {}}
                   onChange={v => setSection("footer", { contact_label: v })} />
          <FooterLinks title="Explore links" prop="links"
                       value={data.footer?.links || []}
                       onChange={v => setSection("footer", { links: v })} />
          <FooterLinks title="Legal links" prop="legal_links"
                       value={data.footer?.legal_links || []}
                       onChange={v => setSection("footer", { legal_links: v })} />
        </>)}
      </Box>

      <Box title="Brand assets" hint="Public-facing visual identity (header + footer logo, favicon, social share image, alt text). Editable without a code deploy; preserve the current file until a replacement is saved.">
        <TextRow label="Header logo URL (image or /api/media/… path)"
                 value={data.brand_assets?.header_logo || ""}
                 placeholder="/images/logo-full.webp"
                 onChange={v => setSection("brand_assets", { header_logo: v })} />
        <TextRow label="Footer logo URL"
                 value={data.brand_assets?.footer_logo || ""}
                 placeholder="/images/logo-full.webp"
                 onChange={v => setSection("brand_assets", { footer_logo: v })} />
        <TextRow label="Favicon URL (ICO/SVG/PNG)"
                 value={data.brand_assets?.favicon || ""}
                 placeholder="/favicon.svg"
                 onChange={v => setSection("brand_assets", { favicon: v })} />
        <TextRow label="Default social share image URL (Open Graph)"
                 value={data.brand_assets?.social_share_image || ""}
                 placeholder="/images/og-default.jpg"
                 onChange={v => setSection("brand_assets", { social_share_image: v })} />
        <I18nRow label="Logo alt text"
                 value={data.brand_assets?.logo_alt || {}}
                 onChange={v => setSection("brand_assets", { logo_alt: v })} />
        <I18nRow label="Social share image alt text"
                 value={data.brand_assets?.social_share_alt || {}}
                 onChange={v => setSection("brand_assets", { social_share_alt: v })} />
      </Box>

      <Box title="Campaign bar" hint="Compact persistent fundraising bar shown directly below the main navigation on every public page (hidden on /admin and the retired KB Hallen route). Progress is manual — set the current donated amount yourself; it does not read raffle ticket availability or Stripe.">
        <Toggle label="Show campaign bar" checked={data.campaign_bar?.show !== false}
                onChange={v => setSection("campaign_bar", { show: v })} testid="toggle-campaign-bar" />
        {data.campaign_bar?.show !== false && (<>
          <I18nRow label="Message"
                   value={data.campaign_bar?.message || {}}
                   onChange={v => setSection("campaign_bar", { message: v })} />
          <div className="grid md:grid-cols-2 gap-3">
            <TextRow label="Target amount (DKK)"
                     value={String(data.campaign_bar?.target_amount_dkk ?? "")}
                     placeholder="50000"
                     onChange={v => setSection("campaign_bar", { target_amount_dkk: Number(v) || 0 })} />
            <TextRow label="Current donated amount (DKK)"
                     value={String(data.campaign_bar?.current_amount_dkk ?? "")}
                     placeholder="35000"
                     onChange={v => setSection("campaign_bar", { current_amount_dkk: Number(v) || 0 })} />
          </div>
          <I18nRow label="Progress label (e.g. 35,000 / 50,000 DKK)"
                   value={data.campaign_bar?.amount_format || {}}
                   onChange={v => setSection("campaign_bar", { amount_format: v })} />
          <I18nRow label="Remaining label"
                   value={data.campaign_bar?.remaining_label || {}}
                   onChange={v => setSection("campaign_bar", { remaining_label: v })} />
          <I18nRow label="Button label"
                   value={data.campaign_bar?.cta_label || {}}
                   onChange={v => setSection("campaign_bar", { cta_label: v })} />
          <TextRow label="Button destination"
                   value={data.campaign_bar?.cta_url || ""}
                   placeholder="/raffle#buy"
                   onChange={v => setSection("campaign_bar", { cta_url: v })} />
          <TextRow label="Auto-hide after (ISO datetime, optional)"
                   value={data.campaign_bar?.end_date || ""}
                   placeholder="2026-10-25T23:59:00Z"
                   onChange={v => setSection("campaign_bar", { end_date: v })} />
        </>)}
      </Box>

      <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-cream/90 backdrop-blur -mx-6 px-6 py-4 border-t border-line">
        {msg && <span className="text-xs text-ink/60" data-testid="site-settings-msg">{msg}</span>}
        <button onClick={save} disabled={saving} data-testid="site-settings-save"
                className="bg-terracotta hover:bg-terracotta-dark text-white px-5 py-2 text-sm uppercase tracking-widest disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 bg-white border border-line rounded-lg px-5 py-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
             data-testid={testid} className="w-4 h-4 accent-terracotta" />
      <span className="font-editorial text-base">{label}</span>
    </label>
  );
}

function Box({ title, hint, children }) {
  return (
    <div className="bg-white border border-line rounded-lg p-5 space-y-3">
      <div>
        <div className="label-mono text-terracotta text-[10px]">{title.toUpperCase()}</div>
        {hint && <div className="text-[11px] text-ink/50 mt-0.5">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function TextRow({ label, value, onChange, placeholder }) {
  return (
    <div>
      <div className="label-mono text-ink/60 text-[10px] mb-1">{label}</div>
      <input value={value} placeholder={placeholder}
             onChange={e => onChange(e.target.value)}
             className="w-full border border-line px-3 py-2 text-sm" />
    </div>
  );
}

function I18nRow({ label, value, onChange }) {
  return (
    <div>
      <div className="label-mono text-ink/60 text-[10px] mb-1">{label}</div>
      <div className="grid grid-cols-3 gap-2">
        {["en", "da", "es"].map(l => (
          <input key={l} value={value?.[l] || ""}
                 placeholder={l.toUpperCase()}
                 onChange={e => onChange({ ...(value || {}), [l]: e.target.value })}
                 className="w-full border border-line px-3 py-2 text-sm" />
        ))}
      </div>
    </div>
  );
}

function FooterLinks({ title, prop, value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const setRow = (i, patch) => onChange(rows.map((r, j) => j === i ? { ...r, ...patch } : r));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, { show: true, url: "/", label: { en: "New link", da: "", es: "" } }]);
  return (
    <div>
      <div className="label-mono text-ink/60 text-[10px] mb-2">{title}</div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[auto,1fr,1fr,1fr,1fr,auto] gap-2 mb-2 items-center">
          <input type="checkbox" checked={r.show !== false}
                 onChange={e => setRow(i, { show: e.target.checked })}
                 data-testid={`footer-${prop}-show-${i}`}
                 className="w-4 h-4 accent-terracotta" />
          <input value={r.url || ""} placeholder="/url"
                 onChange={e => setRow(i, { url: e.target.value })}
                 data-testid={`footer-${prop}-url-${i}`}
                 className="border border-line px-2 py-1.5 text-xs" />
          {["en", "da", "es"].map(l => (
            <input key={l} value={r.label?.[l] || ""} placeholder={l.toUpperCase()}
                   onChange={e => setRow(i, { label: { ...(r.label || {}), [l]: e.target.value } })}
                   data-testid={`footer-${prop}-label-${l}-${i}`}
                   className="border border-line px-2 py-1.5 text-xs" />
          ))}
          <button onClick={() => remove(i)} className="text-xs text-red-600"
                  data-testid={`footer-${prop}-remove-${i}`}>×</button>
        </div>
      ))}
      <button onClick={add} className="text-xs text-terracotta"
              data-testid={`footer-${prop}-add`}>+ Add link</button>
    </div>
  );
}
